/** Console v0.5: Team (design brief v0.5 s.5-6, the Team mock-up), on
 * session A's routes: invites (#367, CAP-009.H6a) and people and approval
 * rules (#373, CAP-009.H6b), and Preekie service-team access (#382, CAP-009.H6c). Who
 * may change the team, the limits, the last-admin and approval rules are all
 * the server's; the controls are only hidden here. */

import { ApiError } from "../api.js";
import type { ScreenContext } from "./app.js";
import { SCREENS } from "./app.js";
import { isAccountAdmin } from "./routes.js";
import {
  createInvite,
  fetchServiceStaff,
  fetchTeam,
  grantServiceAccess,
  removeMember,
  resendInvite,
  revokeInvite,
  setRules,
  updateMember,
  withdrawServiceAccess,
} from "./team-api.js";
import type { InviteResult, Person, ServiceGrant, TeamData } from "./team-api.js";
import {
  CAPABILITIES,
  COPY,
  DEFAULT_CAPABILITIES,
  canResend,
  canRevoke,
  deliveryNotice,
  inviteBody,
  inviteDetail,
  inviteFormProblem,
  inviteStateLabel,
  PEOPLE_COPY,
  accessLabel,
  capabilityLabels,
  memberChange,
  personName,
  personRef,
  seatNote,
  SERVICE_COPY,
  SERVICE_NAMES,
  grantProblem,
  grantScope,
  shortDate,
} from "./team-logic.js";
import type { Capability, Invite } from "./team-logic.js";
import { avatar, button, card, emptyState, figure, h, legalFooter, loading, notice, openDialog, pageHead, table, tabs, tag, toggleRow, uid } from "./ui.js";
import { initials } from "./routes.js";

type Tab = "people" | "invites" | "service" | "rules";

function messageOf(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function team(ctx: ScreenContext): () => void {
  const { me } = ctx;
  const type = me.account?.type ?? "practice";
  const admin = isAccountAdmin(me);
  let tab: Tab = "people";
  let data: TeamData | null = null;
  let invites: Invite[] | null = null;
  let savingRule = false;
  let loadError: string | null = null;
  let flash: { text: string; ok: boolean; link?: string } | null = null;
  // After an action whose control is redrawn away (a dialog's button, a row's Resend), focus goes to the result.
  let focusFlash = false;
  let gone = false;

  const intro =
    type === "practice"
      ? "Your practice staff, what each person can do, and which organisations they can see."
      : "The people in your business who use Preekie, and what each person can do.";

  async function load(): Promise<void> {
    if (!admin) return draw(); // GET /api/team is account admins only (403 not_account_admin)
    try {
      data = await fetchTeam();
      invites = data.invites;
      loadError = null;
    } catch (err) {
      loadError = messageOf(err, "Preekie couldn't load your team. Try again in a few minutes.");
    }
    draw();
  }

  /** "All organisations, including new ones" or a chosen list: the practice's organisation picker (invite and edit). */
  function orgPicker(initial: readonly string[] | null): { node: HTMLElement; value: () => string[] | null; chosenCount: () => number } {
    const group = `pk5-orgs-${uid("g")}`;
    const all = h("input");
    all.type = "radio";
    all.name = group;
    all.checked = initial === null;
    const chosen = h("input");
    chosen.type = "radio";
    chosen.name = group;
    chosen.checked = initial !== null;
    const list = h("div", "pk5-check-list");
    list.setAttribute("role", "group");
    list.setAttribute("aria-label", "Organisations");
    const boxes = new Map<string, HTMLInputElement>();
    const entities = me.entities ?? [];
    if (!entities.length) list.append(h("p", "pk5-muted", "No organisations yet."));
    for (const e of entities) {
      const box = h("input");
      box.type = "checkbox";
      box.checked = initial?.includes(e.client_slug) ?? false;
      boxes.set(e.client_slug, box);
      list.append(h("label", "pk5-check", box, e.client_name));
    }
    const sync = () => {
      list.hidden = all.checked;
    };
    all.addEventListener("change", sync);
    chosen.addEventListener("change", sync);
    sync();
    const ids = () => [...boxes].filter(([, b]) => b.checked).map(([id]) => id);
    const node = h(
      "fieldset",
      "pk5-fieldset",
      h("legend", "pk5-label", COPY.whichOrganisations),
      h("label", "pk5-check", all, COPY.allOrganisations),
      h("label", "pk5-check", chosen, COPY.chosenOrganisations),
      list
    );
    return { node, value: () => (all.checked ? null : ids()), chosenCount: () => ids().length };
  }

  function capabilityBoxes(initial: readonly string[]): { node: HTMLElement; value: () => Capability[] } {
    const boxes = new Map<Capability, HTMLInputElement>();
    const row = h("div", "pk5-check-row");
    for (const c of CAPABILITIES) {
      const box = h("input");
      box.type = "checkbox";
      box.checked = initial.includes(c.id);
      boxes.set(c.id, box);
      row.append(h("label", "pk5-check", box, c.label));
    }
    return {
      node: h("fieldset", "pk5-fieldset", h("legend", "pk5-label", COPY.whatCanTheyDo), row),
      value: () => [...boxes].filter(([, b]) => b.checked).map(([c]) => c),
    };
  }

  function openEdit(p: Person): void {
    const caps = capabilityBoxes(p.capabilities);
    const orgs = type === "practice" ? orgPicker(p.organisation_ids) : null;
    const problem = h("p", "pk5-form-error");
    problem.setAttribute("role", "alert");
    problem.hidden = true;
    const show = (text: string) => {
      problem.textContent = text;
      problem.hidden = false;
    };
    const who = personRef(p);
    const shown = personName(p);
    const body: Node[] = [h("p", "pk5-muted", p.email && p.email !== shown ? `${shown} · ${p.email}` : shown), caps.node];
    if (orgs) body.push(orgs.node);
    body.push(problem);
    if (!p.is_you) {
      const remove = button(`Remove ${who} from the team`, "destructive", () => {
        dialog.close();
        confirmRemove(p);
      });
      remove.classList.add("pk5-dialog-aside");
      body.push(remove);
    }
    const dialog = openDialog(`Edit ${who}`, body, [
      { label: "Cancel", variant: "secondary", safe: true, onClick: (close) => close() },
      {
        label: "Save changes",
        variant: "primary",
        onClick: async (close, btn) => {
          const capabilities = caps.value();
          if (!capabilities.length) return show(COPY.pickOne);
          const organisation_ids = orgs ? orgs.value() : p.organisation_ids;
          if (orgs && organisation_ids !== null && organisation_ids.length === 0) return show(COPY.pickOrganisation);
          const change = memberChange(p, { capabilities, organisation_ids });
          if (!change) return close();
          btn.disabled = true;
          try {
            await updateMember(p.user_id, change);
            flash = { text: `Changes to ${who} saved. They apply from their next click.`, ok: true };
            focusFlash = true;
            close();
            await load();
          } catch (err) {
            // e.g. 409 last_admin: the server's own words; the dialog stays open.
            show(messageOf(err, "The changes weren't saved. Try again in a few minutes."));
            btn.disabled = false;
          }
        },
      },
    ]);
  }

  function confirmRemove(p: Person): void {
    const who = personRef(p);
    openDialog(`Remove ${who}?`, [h("p", null, PEOPLE_COPY.removeBody)], [
      { label: "Keep them", variant: "secondary", safe: true, onClick: (close) => close() },
      {
        label: "Remove",
        variant: "destructive",
        onClick: async (close, btn) => {
          btn.disabled = true;
          try {
            await removeMember(p.user_id);
            flash = { text: `${personRef(p, true)} was removed from the team.`, ok: true };
          } catch (err) {
            flash = { text: messageOf(err, "They weren't removed. Try again in a few minutes."), ok: false };
          }
          focusFlash = true;
          close();
          await load();
        },
      },
    ]);
  }

  function peoplePanel(): Node[] {
    if (!admin) return [card(undefined, emptyState("Team", PEOPLE_COPY.adminsOnly))];
    if (loadError) return [notice(loadError, "alert")];
    if (!data) return [loading()];
    const accountName = me.account?.name ?? null;
    const rows = data.people.map((p) => {
      const name = personName(p);
      const person = h(
        "span",
        "pk5-person",
        avatar(p.display_name || p.email ? initials(p.display_name, p.email) : "–"),
        h("span", "pk5-person-text", h("span", "pk5-person-name", name, p.is_you ? h("span", "pk5-you", " (you)") : null), p.email && p.email !== name ? h("span", "pk5-muted", p.email) : null)
      );
      const caps = h("span", "pk5-tags", ...capabilityLabels(p.capabilities).map((l) => tag(l)));
      const edit = button("Edit", "secondary", () => openEdit(p));
      edit.setAttribute("aria-label", `Edit ${personRef(p)}`);
      return [person, caps, accessLabel(p.organisation_ids, type, accountName), tag(PEOPLE_COPY.twoFactorRequired, "good"), edit];
    });
    const note = seatNote(data.limits);
    return [
      table("People", [{ label: "Person" }, { label: "Can do" }, { label: "Organisation access" }, { label: "Two-factor" }, { label: "Actions" }], rows),
      ...(note ? [h("p", "pk5-muted", note)] : []),
    ];
  }

  function openGrant(): void {
    const who = h("select", "pk5-select");
    const whoId = uid("pk5-staff");
    who.id = whoId;
    who.append(new Option("Loading…", ""));
    void fetchServiceStaff()
      .then((r) => {
        who.replaceChildren(new Option(r.staff.length ? "Choose a person" : "No service staff available yet", ""));
        for (const st of r.staff) who.append(new Option(st.display_name, st.user_id));
      })
      .catch((err: unknown) => {
        who.replaceChildren(new Option("Couldn't load the service team", ""));
        show(messageOf(err, "Preekie couldn't load the service team."));
      });
    const whoLabel = h("label", "pk5-label", "Who");
    whoLabel.htmlFor = whoId;
    const orgBoxes = new Map<string, HTMLInputElement>();
    const orgList = h("div", "pk5-check-list");
    orgList.setAttribute("role", "group");
    orgList.setAttribute("aria-label", "Organisations");
    for (const e of me.entities ?? []) {
      const box = h("input");
      box.type = "checkbox";
      box.checked = type === "company"; // a company's one organisation
      orgBoxes.set(e.client_slug, box);
      orgList.append(h("label", "pk5-check", box, e.client_name));
    }
    const svcBoxes = new Map<string, HTMLInputElement>();
    const svcRow = h("div", "pk5-check-row");
    for (const sv of SERVICE_NAMES) {
      const box = h("input");
      box.type = "checkbox";
      svcBoxes.set(sv.id, box);
      svcRow.append(h("label", "pk5-check", box, sv.label));
    }
    const days = h("input", "pk5-input pk5-figure");
    days.type = "number";
    days.min = "1";
    days.max = "90";
    days.value = "14";
    const daysId = uid("pk5-days");
    days.id = daysId;
    const daysLabel = h("label", "pk5-label", "Days of access");
    daysLabel.htmlFor = daysId;
    const problem = h("p", "pk5-form-error");
    problem.setAttribute("role", "alert");
    problem.hidden = true;
    const show = (text: string) => {
      problem.textContent = text;
      problem.hidden = false;
    };
    const body: Node[] = [h("div", "pk5-field", whoLabel, who)];
    if (type === "practice") body.push(h("fieldset", "pk5-fieldset", h("legend", "pk5-label", COPY.whichOrganisations), orgList));
    body.push(
      h("fieldset", "pk5-fieldset", h("legend", "pk5-label", "Which services?"), svcRow),
      h("div", "pk5-field", daysLabel, days, h("p", "pk5-muted", SERVICE_COPY.daysHint)),
      h("p", "pk5-muted", `${SERVICE_COPY.canApprove}: ${SERVICE_COPY.canApproveAnswer}`),
      problem
    );
    openDialog(`${SERVICE_COPY.grant}: ${SERVICE_COPY.title}`, body, [
      { label: "Cancel", variant: "secondary", safe: true, onClick: (close) => close() },
      {
        label: SERVICE_COPY.grant,
        variant: "primary",
        onClick: async (close, btn) => {
          const g = {
            userId: who.value,
            organisationIds: [...orgBoxes].filter(([, b]) => b.checked).map(([id]) => id),
            services: [...svcBoxes].filter(([, b]) => b.checked).map(([id]) => id),
            days: Number(days.value),
          };
          const bad = grantProblem(g);
          if (bad) return show(bad);
          btn.disabled = true;
          try {
            const r = await grantServiceAccess({ user_id: g.userId, organisation_ids: g.organisationIds, services: g.services, days: g.days });
            flash = { text: `Access granted to ${r.grant.display_name ?? "the service team"} until ${shortDate(r.grant.expires_at, ctx.timeZone)}.`, ok: true };
            focusFlash = true;
            close();
            await load();
          } catch (err) {
            // 400 invalid_grant, 409 grant_exists, 404 outside reach: the server's words, dialog stays open.
            show(messageOf(err, "Access wasn't granted. Try again in a few minutes."));
            btn.disabled = false;
          }
        },
      },
    ]);
    who.focus();
  }

  function confirmWithdraw(g: ServiceGrant): void {
    const name = g.display_name ?? "this person";
    openDialog(`Withdraw access for ${name}?`, [h("p", null, SERVICE_COPY.withdrawBody)], [
      { label: "Keep access", variant: "secondary", safe: true, onClick: (close) => close() },
      {
        label: "Withdraw access",
        variant: "destructive",
        onClick: async (close, btn) => {
          btn.disabled = true;
          try {
            await withdrawServiceAccess(g.grant_id, g.user_id);
            flash = { text: `Access withdrawn for ${name}.`, ok: true };
          } catch (err) {
            flash = { text: messageOf(err, "Access wasn't withdrawn. Try again in a few minutes."), ok: false };
          }
          focusFlash = true;
          close();
          await load();
        },
      },
    ]);
  }

  function servicePanel(): Node[] {
    if (!admin) return [card(undefined, emptyState(SERVICE_COPY.title, PEOPLE_COPY.adminsOnly))];
    if (loadError) return [notice(loadError, "alert")];
    if (!data) return [loading()];
    const active = data.service_grants.filter((g) => g.status === "active");
    const past = data.service_grants.filter((g) => g.status !== "active");
    const id = uid("pk5-svc-h");
    const heading = h("h2", "pk5-section-title", SERVICE_COPY.title);
    heading.id = id;
    const section = card(
      undefined,
      h("div", "pk5-card-head", heading, tag(active.length ? SERVICE_COPY.granted : SERVICE_COPY.notGranted, active.length ? "good" : "neutral")),
      h("p", null, SERVICE_COPY.body)
    );
    section.setAttribute("aria-labelledby", id);
    if (!active.length) section.append(h("dl", "pk5-facts", h("dt", null, "Scope"), h("dd", null, SERVICE_COPY.none)));
    for (const g of active) {
      const withdraw = button("Withdraw access", "destructive", () => confirmWithdraw(g));
      withdraw.setAttribute("aria-label", `Withdraw access for ${g.display_name ?? "this person"}`);
      section.append(
        h(
          "div",
          "pk5-grant",
          h(
            "dl",
            "pk5-facts",
            h("dt", null, "Who"),
            h("dd", null, g.display_name ?? "–"),
            h("dt", null, "Scope"),
            h("dd", null, grantScope(g.services, g.organisation_ids.length, type)),
            h("dt", null, "Until"),
            h("dd", null, figure(shortDate(g.expires_at, ctx.timeZone))),
            h("dt", null, SERVICE_COPY.canApprove),
            h("dd", null, SERVICE_COPY.canApproveAnswer)
          ),
          withdraw
        )
      );
    }
    section.append(h("div", "pk5-detail-actions", button(SERVICE_COPY.grant, "primary", openGrant)));
    const parts: Node[] = [section];
    if (past.length) {
      parts.push(
        table(
          "Earlier access",
          [{ label: "Who" }, { label: "Scope" }, { label: "Ended" }],
          past.map((g) => [
            g.display_name ?? "–",
            grantScope(g.services, g.organisation_ids.length, type),
            `${g.status === "withdrawn" ? "Withdrawn" : "Expired"} ${shortDate(g.withdrawn_at ?? g.expires_at, ctx.timeZone)}`,
          ])
        )
      );
    }
    return parts;
  }

  function rulesPanel(): Node[] {
    if (!admin) return [card(undefined, emptyState("Approval rules", PEOPLE_COPY.adminsOnly))];
    if (loadError) return [notice(loadError, "alert")];
    if (!data) return [loading()];
    const fixed = (label: string, hint: string) =>
      h("div", "pk5-toggle-row pk5-toggle-fixed", h("span", "pk5-toggle-text", h("span", "pk5-toggle-label", label), h("span", "pk5-toggle-hint", hint)), tag(PEOPLE_COPY.alwaysOn));
    const self = toggleRow(PEOPLE_COPY.selfApproval, PEOPLE_COPY.selfApprovalHint, data.rules.self_approval_allowed, savingRule ? null : (on) => void saveRule(on));
    const id = uid("pk5-rules-h");
    const heading = h("h2", "pk5-section-title", "Approval rules");
    heading.id = id;
    const section = card(undefined, heading, fixed(PEOPLE_COPY.differentPerson, PEOPLE_COPY.differentPersonHint), self, fixed(PEOPLE_COPY.lodging, PEOPLE_COPY.lodgingHint));
    section.setAttribute("aria-labelledby", id);
    return [section];
  }

  async function saveRule(on: boolean): Promise<void> {
    savingRule = true;
    try {
      const r = await setRules(on);
      if (data) data.rules = r.rules;
      flash = { text: on ? "Self-approval is allowed when only one approver exists. Each one is recorded with a warning." : "Self-approval is off. A different person must approve.", ok: true };
    } catch (err) {
      flash = { text: messageOf(err, "The approval rule wasn't changed. Try again in a few minutes."), ok: false };
    }
    savingRule = false;
    focusFlash = true;
    await load();
  }

  function pendingCount(): number | null {
    return invites ? invites.filter((i) => i.status === "pending" || i.status === "accepting").length : null;
  }

  function afterSend(result: InviteResult): void {
    const d = deliveryNotice(result.delivery ?? "sent");
    flash = { text: d.text, ok: d.ok, link: result.test_accept_url };
    focusFlash = true;
  }

  function openInvite(): void {
    const email = h("input", "pk5-input");
    email.type = "email";
    email.autocomplete = "off";
    email.placeholder = "name@example.com";
    const emailId = uid("pk5-invite-email");
    email.id = emailId;
    const emailLabel = h("label", "pk5-label", COPY.workEmail);
    emailLabel.htmlFor = emailId;

    const caps = capabilityBoxes(DEFAULT_CAPABILITIES);
    const body: Node[] = [h("div", "pk5-field", emailLabel, email), caps.node];
    const orgs = type === "practice" ? orgPicker([]) : null;
    if (orgs) body.push(orgs.node);
    const problem = h("p", "pk5-form-error");
    problem.setAttribute("role", "alert");
    problem.hidden = true;
    body.push(h("p", "pk5-muted", COPY.inviteFootnote), problem);

    const show = (text: string) => {
      problem.textContent = text;
      problem.hidden = false;
    };

    openDialog(COPY.invite, body, [
      { label: "Cancel", variant: "secondary", safe: true, onClick: (close) => close() },
      {
        label: "Send invite",
        variant: "primary",
        onClick: async (close, btn) => {
          const chosen = orgs?.value() ?? null;
          const form = {
            email: email.value,
            capabilities: caps.value(),
            allOrganisations: chosen === null,
            organisationIds: chosen ?? [],
          };
          const bad = inviteFormProblem(form, type);
          if (bad) {
            show(bad);
            return;
          }
          btn.disabled = true;
          btn.textContent = "Sending…";
          try {
            const result = await createInvite(inviteBody(form, type));
            afterSend(result);
            close();
            tab = "invites";
            await load();
          } catch (err) {
            // e.g. 409 limit_reached / already_invited: the server's exact words; the dialog stays open.
            show(messageOf(err, "The invite wasn't sent. Try again in a few minutes."));
            btn.disabled = false;
            btn.textContent = "Send invite";
          }
        },
      },
    ]);
    email.focus();
  }

  async function resend(inv: Invite): Promise<void> {
    try {
      afterSend(await resendInvite(inv.invite_id));
    } catch (err) {
      flash = { text: messageOf(err, "The invite wasn't sent again. Try again in a few minutes."), ok: false };
      focusFlash = true;
    }
    await load();
  }

  function cancel(inv: Invite): void {
    openDialog(COPY.cancelQuestion, [h("p", null, inv.email), h("p", null, COPY.cancelBody)], [
      { label: COPY.keepInvite, variant: "secondary", safe: true, onClick: (close) => close() },
      {
        label: COPY.cancelInvite,
        variant: "destructive",
        onClick: async (close, btn) => {
          btn.disabled = true;
          try {
            await revokeInvite(inv.invite_id);
            flash = { text: `Invite to ${inv.email} cancelled.`, ok: true };
          } catch (err) {
            flash = { text: messageOf(err, "The invite wasn't cancelled. Try again in a few minutes."), ok: false };
          }
          focusFlash = true;
          close();
          await load();
        },
      },
    ]);
  }

  function invitesPanel(): Node[] {
    if (!admin) return [card(undefined, emptyState("Pending invites", PEOPLE_COPY.adminsOnly))];
    if (loadError) return [notice(loadError, "alert")];
    if (!invites) return [loading()];
    const shown = invites.filter((i) => i.status !== "used" && i.status !== "revoked");
    if (!shown.length) return [card(undefined, emptyState(COPY.noInvites, admin ? COPY.noInvitesBody : undefined)), h("p", "pk5-muted", COPY.invitesExpire)];
    const list = h("ul", "pk5-invite-list");
    for (const inv of shown) {
      const actions = h("span", "pk5-invite-actions");
      if (admin && canResend(inv.status)) {
        const b = button(COPY.resend, "secondary", () => void resend(inv));
        b.setAttribute("aria-label", `${COPY.resend} to ${inv.email}`);
        actions.append(b);
      }
      if (admin && canRevoke(inv.status)) {
        const b = button(COPY.cancelInvite, "destructive", () => cancel(inv));
        b.setAttribute("aria-label", `${COPY.cancelInvite} to ${inv.email}`);
        actions.append(b);
      }
      list.append(
        h(
          "li",
          "pk5-invite",
          h("span", "pk5-invite-text", h("span", "pk5-invite-email", inv.email), h("span", "pk5-muted", inviteDetail(inv, type))),
          h("span", "pk5-invite-state pk5-figure", inviteStateLabel(inv, ctx.timeZone)),
          actions
        )
      );
    }
    return [card("pk5-card-flush", list), h("p", "pk5-muted", COPY.invitesExpire)];
  }


  function draw(): void {
    if (gone) return;
    const actions = admin ? [button(COPY.invite, "primary", openInvite)] : [];
    const panelId = uid("pk5-team-panel");
    const panel = h("div", "pk5-tab-panel");
    panel.id = panelId;
    panel.setAttribute("role", "tabpanel");
    const content =
      tab === "invites"
        ? invitesPanel()
        : tab === "people"
          ? peoplePanel()
          : tab === "service"
            ? servicePanel()
            : rulesPanel();
    panel.append(...content);
    const flashNode = flash
      ? notice(
          h("span", null, flash.text, flash.link ? h("span", "pk5-test-link", " ", linkTo(flash.link)) : null),
          flash.ok ? "status" : "alert"
        )
      : null;
    ctx.root.replaceChildren(
      pageHead("Team", intro, actions),
      ...(flashNode ? [flashNode] : []),
      tabs(
        "Team sections",
        [
          { id: "people", label: "People", count: data ? data.people.length : null },
          { id: "invites", label: "Pending invites", count: pendingCount() },
          { id: "service", label: "Preekie service team" },
          { id: "rules", label: "Approval rules" },
        ],
        tab,
        (t) => {
          tab = t;
          draw();
        },
        panelId
      ),
      panel,
      legalFooter()
    );
    if (focusFlash && flashNode) {
      focusFlash = false;
      flashNode.tabIndex = -1;
      flashNode.focus();
    }
  }

  function linkTo(url: string): HTMLAnchorElement {
    // TEST only (A returns it for *@preekie.test in test): the accept link, since no email goes to a test domain.
    const a = h("a", null, "Accept link (test only)");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    return a;
  }

  draw();
  void load();
  return () => {
    gone = true;
  };
}

SCREENS.team = team;
